import { useEffect, useState } from "react";
import { api, money, errorMessage } from "../api";
import { Check, Crown, Loader2, ShieldCheck, Sparkles } from "lucide-react";

const CYCLE_ORDER = ["monthly", "half_yearly", "yearly"];

function discountLabel(cycle, prices) {
  if (cycle === "monthly") return "";
  const monthly12 = prices.monthly * 12;
  if (cycle === "yearly") {
    const saving = Math.max(0, Math.round(100 - (prices.yearly / monthly12) * 100));
    return saving > 0 ? `Save ${saving}% vs monthly` : "";
  }
  const monthly6 = prices.monthly * 6;
  const saving = Math.max(0, Math.round(100 - (prices.half_yearly / monthly6) * 100));
  return saving > 0 ? `Save ${saving}% vs monthly` : "";
}

export default function Plans({ entitlements, trialExpired, onActivated }) {
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/plans").then(r => setData(r.data)).catch(() => {}); }, []);

  const subscribe = async (planId) => {
    setBusy(planId); setErr("");
    try {
      const { data: intent } = await api.post("/subscribe/checkout", { plan_id: planId, cycle });
      if (intent.mock) {
        // Mock pay flow: one-click activation
        await api.post("/subscribe/mock-pay", { order_id: intent.order_id });
        await onActivated?.();
      } else {
        // Real Razorpay flow — load SDK on demand
        if (!window.Razorpay) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://checkout.razorpay.com/v1/checkout.js";
            s.onload = resolve; s.onerror = reject; document.body.appendChild(s);
          });
        }
        const options = {
          key: intent.key_id, amount: intent.amount_paise, currency: intent.currency,
          name: intent.app_name, description: `${intent.plan_name} · ${intent.cycle_label}`,
          order_id: intent.order_id,
          handler: async (res) => {
            try {
              await api.post("/subscribe/verify", {
                order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              await onActivated?.();
            } catch (e) { setErr(errorMessage(e)); }
          },
          theme: { color: "#c05a26" },
        };
        new window.Razorpay(options).open();
      }
    } catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="plans-shell" data-testid="plans-loading"><Loader2 className="spin" size={22}/> Loading plans…</div>;

  const groups = {};
  for (const f of data.feature_catalog) { (groups[f.group] = groups[f.group] || []).push(f); }

  return (
    <div className="plans-shell" data-testid="plans-view">
      {trialExpired && (
        <div className="paywall-banner" data-testid="paywall-banner">
          <ShieldCheck size={16}/> Your 7-day free trial has ended. Choose a plan to continue running your restaurant on FynDine.
        </div>
      )}
      <div className="plans-head">
        <span className="overline">PRICING</span>
        <h1>Simple, honest pricing for Indian restaurants.</h1>
        <p>Billed by {data.legal_entity}. Taxes extra as applicable. Switch, upgrade or downgrade anytime.</p>
        <div className="cycle-toggle" role="tablist" aria-label="Billing cycle">
          {CYCLE_ORDER.map((c) => {
            const label = data.cycles.find(x => x.id === c)?.label || c;
            return <button key={c} className={cycle === c ? "cycle on" : "cycle"} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}>{label}</button>;
          })}
        </div>
      </div>

      <div className="plan-grid">
        {data.plans.map((p) => {
          const price = p.prices[cycle];
          const isCurrent = entitlements?.plan_id === p.id && entitlements?.source === "paid";
          return (
            <article key={p.id} className={`plan-card ${p.popular ? "popular" : ""} ${isCurrent ? "current" : ""}`} data-testid={`plan-card-${p.id}`}>
              {p.popular && <span className="plan-ribbon"><Crown size={11}/> Most popular</span>}
              <header>
                <h3>{p.name}</h3>
                <p>{p.tagline}</p>
              </header>
              <div className="plan-price">
                <strong>{money(price)}</strong>
                <small>{data.cycles.find(x => x.id === cycle)?.label}</small>
                {discountLabel(cycle, p.prices) && <em>{discountLabel(cycle, p.prices)}</em>}
              </div>
              <ul className="plan-feats">
                {p.features.slice(0, 10).map((f) => {
                  const label = data.feature_catalog.find(x => x.key === f)?.label || f;
                  return <li key={f}><Check size={12}/> {label}</li>;
                })}
                {p.features.length > 10 && <li className="more">+ {p.features.length - 10} more</li>}
                <li className="more">Up to <b>{p.max_branches}</b> {p.max_branches === 1 ? "outlet" : "outlets"}</li>
              </ul>
              <button className="plan-cta" disabled={busy === p.id || isCurrent} onClick={() => subscribe(p.id)} data-testid={`subscribe-${p.id}`}>
                {busy === p.id ? <Loader2 className="spin" size={14}/> : <Sparkles size={14}/>}
                {isCurrent ? "Current plan" : `Choose ${p.name}`}
              </button>
            </article>
          );
        })}
      </div>

      {err && <div className="auth-alert error" data-testid="plans-error">{err}</div>}

      <section className="plans-breakdown">
        <h2>Full feature breakdown</h2>
        {Object.entries(groups).map(([group, feats]) => (
          <div key={group} className="feature-row">
            <h4>{group}</h4>
            <div className="feature-grid">
              {feats.map(f => (
                <div key={f.key} className="feature-cell">
                  <span className="feature-label">{f.label}</span>
                  {data.plans.map(p => (
                    <span key={p.id} className={`feature-tick ${p.features.includes(f.key) ? "yes" : "no"}`} title={p.name}>{p.features.includes(f.key) ? "✓" : "–"}<small>{p.name[0]}</small></span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      <footer className="plans-foot">
        <small>Operated by {data.legal_entity}. All payments settle to the merchant's bank account via secure gateway.</small>
      </footer>
    </div>
  );
}
